import constants from '@/constants.js'
import chroma from 'chroma-js'
import moment from 'moment'

export const validNumber = num => !(isNaN(num) ||
num === null ||
num === undefined ||
// for our use case, we don't consider Infinity a valid number
num === Infinity ||
num === -Infinity)

export const getMin = (array, key) =>
  Math.min(...array.filter(x => validNumber(x[key])).map(x => Number(x[key])))

export const getMax = (array, key) =>
  Math.max(...array.filter(x => validNumber(x[key])).map(x => Number(x[key])))

export const normalizeValue = (value, min, max) => (value - min) / (max - min)

export const getColor = (param, colorScale, min, max) =>
  chroma
    .scale(colorScale)
    .mode('lch')(normalizeValue(param, min, max))
    .hex()

// add thousands separator to numbers to e.g. turn 1000 into 1,000
// the locale variable, e.g. "en" for English, determines the decimal separator and thousands separator used
// (comma vs. full stop, depending on language/country)
// for the definition of locales, see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#Locale_identification_and_negotiation
export function thousandsSeparator (value, decimalPlaces = 3, locale = 'en', trun = false) {
  if (typeof value === 'string') {
    // TODO remove when fixed in API
    // temp fix for API returning strings in profiles
    if (['development', 'dev_server'].includes(process.env.NODE_ENV)) {
      // log incorrectly formatted values in dev modes
    }
    value === 'null' ? value = null : value = Number(value)
  }
  switch (value) {
    case (typeof value === 'undefined'):
      value = '-'
      break
    case null:
      value = constants.UNAVAIL
      break
    case (typeof value === 'string'):
      // assume strings are the result of numbers already having the function had run on them once
      break
    default:
      // ATTN. this turns numbers into strings
      if (Number.isInteger(value)) {
        // whole numbers should not get decimal places added to them
        value = value.toLocaleString(locale)
      } else {
        value = value.toLocaleString(locale, {
          maximumFractionDigits: decimalPlaces,
          minimumFractionDigits: decimalPlaces
        })
      }
  }
  // Temporary solution to truncate long values with "K" for thousands and "M" for millions
  // until there is an API-side solution
  // TODO: Remove it once API delivers truncated values
  if (trun === true) {
    value = value.replace(/,/g, '')
    let decimalIndex = value.indexOf('.')
    let valueLength = value.length - decimalIndex - 1
    if (valueLength > 6) {
      value = value.substring(0, valueLength - 6) + ' m'
    } else if (valueLength > 3 && valueLength <= 6) {
      value = value.substring(0, valueLength - 3) + ' k'
    }
  }
  return value
}

// get the mouse position when hovering over for visualisations
// so tooltips for meta information can be moved (to avoid cut-off right and bottom)
export function calcTooltipPosition (mousePosition, container, spacingX, spacingY) {
  // assumed average width/height of tooltip
  let elemW = 200
  let elemH = 100
  // parent container (getBoundingClientRect)
  let contW = container.width
  let contH = container.height
  // mouse position (within container!)
  let mouseX = mousePosition.x
  let mouseY = mousePosition.y
  // coordinates for new position
  let x = mouseX + spacingX
  let y = mouseY + spacingY

  let spaceRight = calculateSpace(contW, x, elemW, spacingX)
  let spaceBottom = calculateSpace(contH, y, elemH, spacingY)

  function calculateSpace (containerMeasurement, mousePos, elemMeasurement, spacingVar) {
    return containerMeasurement - mousePos - elemMeasurement - spacingVar
  }

  // if element would get cut off on right, put it to left of cursor
  while (spaceRight <= 0) {
    x -= spacingX
    spaceRight = calculateSpace(contW, x, elemW, spacingX)
  }

  // if element would get cut off at bottom, bring it up
  while (spaceBottom <= 0) {
    y -= spacingY
    spaceBottom = calculateSpace(contH, y, elemH, spacingY)
  }

  return {x: x, y: y}
}

// escape strings containing double quotes or commas in a(nother) by
// wrapping them in another set of double quotes
export function wrapCsvContents (content) {
  if (typeof (content) === 'string') {
    if (content.replace(/ /g, '').match(/[\s,"]/)) {
      return '"' + content.replace(/"/g, '""') + '"'
    }
  }
  return content
}

export function downloadCSV (data) {
  let delimiter = ','

  // generate csv
  let headers = data.headers
  let output = '\uFEFF'
  let contents = ''

  headers.forEach(function (d) {
    contents = wrapCsvContents(d.text)
    output += contents + delimiter
  })
  output = output.slice(0, -1) + '\n'

  data.items.forEach(function (d) {
    headers.forEach(function (e) {
      contents = wrapCsvContents(d[e.value])
      output += contents + delimiter
    })
    output = output.slice(0, -1) + '\n'
  })

  // download functionality
  let a = document.createElement('a')
  let blob = new Blob([output], { encoding: 'UTF-8', type: 'text/csv;charset=UTF-8' })
  a.href = window.URL.createObjectURL(blob)

  let datetime = moment().format('YYYYMMDD_HHmmss')

  a.download = 'KNOWMAK_data_' + datetime + '.csv'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

export function formatISODate (isoDateString) {
  let thisDate = new Date(isoDateString)
  let options = { year: 'numeric', month: 'long', day: 'numeric' }
  return thisDate.toLocaleDateString('en-GB', options)
}



// WEBPACK FOOTER //
// ./src/util.js